import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  BrainIcon, GitForkIcon, SkullIcon, TrophyIcon, WrenchIcon, CodeIcon, SparkleIcon,
} from "@phosphor-icons/react";

const TYPE_META = {
  mutate: { icon: GitForkIcon, color: "#60A5FA", label: "MUTATE" },
  parameter_tune: { icon: WrenchIcon, color: "#D6FF38", label: "TUNE" },
  kill: { icon: SkullIcon, color: "#FF4D4D", label: "KILL" },
  promote: { icon: TrophyIcon, color: "#D6FF38", label: "PROMOTE" },
  code_alter: { icon: CodeIcon, color: "#C084FC", label: "CODE" },
  spawn: { icon: SparkleIcon, color: "#94A3B8", label: "SPAWN" },
};
const ALL_TYPES = Object.keys(TYPE_META);

export default function AIDecisions() {
  const [decisions, setDecisions] = useState([]);
  const [mix, setMix] = useState({ PARAMETER: 0, CODE: 0 });
  const [plans, setPlans] = useState({});
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    api.get("/decisions").then((r) => {
      setDecisions(r.data.decisions);
      setMix(r.data.mutation_mix || { PARAMETER: 0, CODE: 0 });
      setPlans(r.data.plan_counts || {});
    }).catch(console.error);
  }, []);

  const filtered = useMemo(
    () => typeFilter === "all" ? decisions : decisions.filter((d) => d.decision_type === typeFilter),
    [decisions, typeFilter]
  );

  return (
    <div data-testid="decisions-page" className="p-6 space-y-4">
      <div>
        <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">Loop Telemetry</div>
        <h1 className="text-xl md:text-2xl tracking-tight font-medium mt-1">AI Decisions</h1>
      </div>

      {/* Mutation type mix - PARAMETER vs CODE */}
      {(mix.PARAMETER + mix.CODE) > 0 && (
        <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4" data-testid="mutation-mix">
          <div className="text-[10px] mono uppercase tracking-widest text-[#64748B] mb-3">
            Latest run mutation mix · {mix.PARAMETER + mix.CODE} total proposals
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="border border-[#2A2D3A] rounded p-3">
              <div className="text-[10px] mono uppercase tracking-widest text-[#94A3B8]">PARAMETER tweaks</div>
              <div className="mono text-2xl mt-1 text-[#F1F5F9]">{mix.PARAMETER}</div>
              <div className="text-[11px] text-[#94A3B8] mt-1">small numeric changes</div>
            </div>
            <div className="border rounded p-3" style={{ borderColor: mix.CODE > 0 ? "rgba(214,255,56,0.3)" : "#2A2D3A", background: mix.CODE > 0 ? "rgba(214,255,56,0.05)" : "transparent" }}>
              <div className="text-[10px] mono uppercase tracking-widest" style={{ color: mix.CODE > 0 ? "#D6FF38" : "#94A3B8" }}>CODE rewrites</div>
              <div className="mono text-2xl mt-1" style={{ color: mix.CODE > 0 ? "#D6FF38" : "#F1F5F9" }}>{mix.CODE}</div>
              <div className="text-[11px] text-[#94A3B8] mt-1">
                {mix.CODE === 0 ? "AI not proposing CODE - update quant_knowledge.md" : "structural surgery on .cs files"}
              </div>
            </div>
          </div>
          {Object.keys(plans).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {Object.entries(plans).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                <span key={k} className="text-[10px] mono uppercase tracking-widest px-2 py-0.5 rounded-sm border border-[#2A2D3A] text-[#94A3B8]">
                  {k} <span className="text-[#F1F5F9] ml-1">{v}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-1">
        {["all", ...ALL_TYPES].map((t) => (
          <button
            key={t}
            data-testid={`decision-filter-${t}`}
            onClick={() => setTypeFilter(t)}
            className={`text-[10px] mono uppercase tracking-widest px-2.5 py-1.5 rounded-sm border transition-colors ${
              typeFilter === t
                ? "bg-[#D6FF38]/10 text-[#D6FF38] border-[#D6FF38]/30"
                : "bg-transparent text-[#94A3B8] border-[#2A2D3A] hover:border-[#D6FF38]/40 hover:text-[#F1F5F9]"
            }`}
          >
            {t === "all" ? "All" : TYPE_META[t].label}
          </button>
        ))}
        <div className="ml-auto text-[11px] mono text-[#64748B]">{filtered.length} decisions</div>
      </div>

      <div className="max-w-3xl space-y-3">
        {filtered.map((d, i) => {
          const meta = TYPE_META[d.decision_type] || TYPE_META.spawn;
          const Icon = meta.icon;
          return (
            <div
              key={d.id}
              data-testid={`decision-card-${i}`}
              className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4 hover:border-[#3a3e4f] transition-colors row-fade-in"
              style={{ animationDelay: `${Math.min(i * 25, 600)}ms` }}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-9 h-9 rounded-md border flex items-center justify-center shrink-0"
                  style={{ borderColor: `${meta.color}33`, background: `${meta.color}1A` }}
                >
                  <Icon size={16} color={meta.color} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="text-[10px] mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border"
                      style={{ color: meta.color, borderColor: `${meta.color}40`, background: `${meta.color}1A` }}
                    >
                      {meta.label}
                    </span>
                    <span className="text-[11px] mono text-[#64748B]">{new Date(d.timestamp).toLocaleString()}</span>
                    {d.bot_name && (
                      <Link
                        to={`/bot/${d.bot_id}`}
                        className="text-[11px] mono text-[#94A3B8] hover:text-[#D6FF38] ml-auto"
                      >
                        → {d.bot_name}
                      </Link>
                    )}
                  </div>
                  <div className="text-sm mt-1.5 text-[#F1F5F9]">{d.title}</div>
                  <p className="text-xs mt-1 text-[#94A3B8] leading-relaxed">{d.reasoning}</p>
                </div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center text-[#64748B] mono text-xs py-12">No decisions of this type yet</div>
        )}
      </div>
    </div>
  );
}
